import { Navigate, Route, Routes, useSearchParams } from "react-router-dom";

import { AppShell } from "@/components/app-shell";
import { dismissDeletedAccount, useDeletedAccount, useSession } from "@/lib/session";
import { Login } from "@/routes/login";
import { Register } from "@/routes/register";
import { DeviceApprove, deviceReturnPath } from "@/routes/device";
import { Workers } from "@/routes/workers";
import { Devices } from "@/routes/devices";
import { Events } from "@/routes/events";
import { Account, AccountDeleted } from "@/routes/account";
import { AdminAccounts } from "@/routes/admin/accounts";
import { AdminFleet } from "@/routes/admin/fleet";
import { AdminInvites } from "@/routes/admin/invites";
import { AdminTraffic } from "@/routes/admin/traffic";
import { AdminServices } from "@/routes/admin/services";
import { AdminFeatures } from "@/routes/admin/features";

// Signed out, the login card is the *only* thing mounted — not the only thing
// visible. The previous console hid its chrome with a `hidden` attribute that a
// later `display:flex` quietly overrode, and shipped a logged-out page complete
// with a nav bar and a code form; three smoke runs missed it because they
// checked RPCs, not pixels. Nothing to override if nothing is rendered.
export function App() {
  const session = useSession();
  const deleted = useDeletedAccount();
  if (!session) {
    // Deletion ends the session; say what happened before the login card.
    if (deleted) return <AccountDeleted email={deleted} onDone={dismissDeletedAccount} />;
    return (
      <Routes>
        <Route path="/register" element={<Register />} />
        <Route path="*" element={<Login />} />
      </Routes>
    );
  }

  // The device approval page stands alone like the login card: a person
  // arriving from a device's link has one decision to make, not a console.
  return (
    <Routes>
      <Route path="/device" element={<DeviceApprove />} />
      <Route path="/register" element={<AfterRegistration />} />
      <Route path="*" element={<Console />} />
    </Routes>
  );
}

// A session appearing while the URL is still /register means registration
// just finished. Where to go next is read from the URL in the same render,
// not from a navigate() racing the console's catch-all redirect.
function AfterRegistration() {
  const [params] = useSearchParams();
  return <Navigate to={deviceReturnPath(params.get("next")) || "/"} replace />;
}

function Console() {
  return (
    <AppShell>
      <Routes>
        <Route path="/" element={<Workers />} />
        <Route path="/devices" element={<Devices />} />
        <Route path="/events" element={<Events />} />
        <Route path="/account" element={<Account />} />
        <Route path="/admin" element={<AdminAccounts />} />
        <Route path="/admin/fleet" element={<AdminFleet />} />
        <Route path="/admin/services" element={<AdminServices />} />
        <Route path="/admin/traffic" element={<AdminTraffic />} />
        <Route path="/admin/invites" element={<AdminInvites />} />
        <Route path="/admin/features" element={<AdminFeatures />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AppShell>
  );
}
